import { type FC, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Provider, ErrorBoundary } from '@rollbar/react';

import { type RootState } from '@editor2/reducers/root';
import { fetchCourse, fetchCourseResourceSubtree } from '@editor2/reducers/editor/effects';
import ToastContainer from '@editor2/components/Toast/ToastContainer';
import ReadonlyAlert from '@editor2/components/ReadonlyAlert';
import LoaderWrapper from '@editor2/components/LoaderWrapper';
import ForbiddenAlert from '@editor2/components/ForbiddenAlert';
import { hasAnyActiveChanges } from '@editor2/services/unsavedChangesService';
import { removeActiveUsers } from '@editor2/reducers/editor/editorSlice';
import { getRoomNameByLocation, sanitizeCourseId } from '@editor2/services/helpersService';
import { getCourseTabsOpened, getTabsOpened, registerTab, unregisterTab, touchTab } from '@editor2/services/editorTabsService';
import { debugLog } from '@editor2/services/debugService';

import { appContainer, appGlobalStyles } from './styles';
import Routing from './Routing';
import GlobalLoader from '@editor2/components/GlobalLoader';

import { useAppDispatch } from '@editor2/store';
import { useBeforeUnload } from '@editor2/hooks/useBeforeUnload';
import { useFullStoryInitialize } from '@editor2/hooks/useFullStoryInitialize';
import { useEventListener } from '@editor2/hooks/useEventListener';
import ConflictUpdate from '@editor2/components/ConflictUpdate';

import 'bootstrap/dist/css/bootstrap.min.css';
import { Global } from '@emotion/react';

import ThemeProvider from '@soomo/lib/styles/themes/ThemeProvider';
import themes from '@soomo/lib/styles/themes/index';
import FontProvider from '@soomo/lib/styles/themes/FontProvider';

const rollbarConfig = {
	accessToken: import.meta.env.VITE_ROLLBAR_ACCESS_TOKEN,
	enabled: import.meta.env.VITE_ROLLBAR_ENABLED === 'true',
	captureUncaught: true,
	captureUnhandledRejections: true,
	environment: import.meta.env.VITE_ROLLBAR_ENVIRONMENT
};

interface Props {
	match: {
		params: {
			courseId: string;
		};
	};
}

const EditorApp: FC<Props> = (props) => {
	const { match } = props;
	const { courseId: courseIdParam } = match.params;

	const courseId = sanitizeCourseId(courseIdParam);

	const dispatch = useAppDispatch();
	const location = useLocation();

	const { tabsSync } = useSelector((state: RootState) => state);
	const course = useSelector((state: RootState) => state.courses[courseId]);
	const changes = course?.changes;
	const activeFileUploads = course?.activeFileUploads ?? 0;

	useFullStoryInitialize(Boolean(course?.isCourseLoaded));

	const [tabsOpenedNumber, setTabsOpenedNumber] = useState(0);
	const [showExitPrompt, setShowExitPrompt] = useState(false);

	// UUID assigned to this tab on mount; used for register/unregister/heartbeat
	const tabIdRef = useRef<string | null>(null);

	const otherCoursesOpened = (tabsOpened: { [courseId: string]: number }, courseId: number) => {
		return Object.keys(tabsOpened).filter((x) => x !== `${courseId}`).length !== 0;
	};

	const fetchCourseResource = () => {
		dispatch(fetchCourse(courseId)).then(({ payload }) =>
			dispatch(
				fetchCourseResourceSubtree({
					objectId: payload.headCommit.commit.thing_id,
					depth: 'page',
					courseId
				})
			)
		);
	};

	// Track whether we've already initiated a fetch to avoid double-fetching
	// when the fallback timer fires after initStateReceived.
	const hasFetchedRef = useRef(false);

	useEffect(() => {
		// Read counts BEFORE registering this tab, so we know how many other
		// tabs are already alive.
		const tabsOpened = getTabsOpened();
		const courseTabsOpened = tabsOpened[courseId] || 0;

		const shouldFetch = Object.keys(tabsOpened).length === 0 ||
			(!tabsOpened[courseId] && !otherCoursesOpened(tabsOpened, courseId));

		if (shouldFetch) {
			hasFetchedRef.current = true;
			fetchCourseResource();
		}

		// Register this tab and record its UUID
		tabIdRef.current = registerTab(courseId);
		setTabsOpenedNumber(courseTabsOpened + 1);

		return () => {
			if (tabIdRef.current) {
				unregisterTab(courseId, tabIdRef.current);
			}
		};
	}, []);

	// If other tabs were detected at mount, wait for state sync from them.
	// If we receive init state and this is the only tab for this course, fetch data.
	useEffect(() => {
		const courseTabCount = getCourseTabsOpened(courseId);

		if (tabsSync.initStateReceived && courseTabCount === 1 && !hasFetchedRef.current) {
			hasFetchedRef.current = true;
			fetchCourseResource();
		}
	}, [tabsSync.initStateReceived]);

	// Fallback: if we expected state sync from another tab but it never arrived
	// (e.g. stale tabsOpened counter from a crashed tab), fetch after a timeout.
	useEffect(() => {
		if (hasFetchedRef.current) return;

		const fallbackTimer = setTimeout(() => {
			if (!hasFetchedRef.current && !course?.isCourseLoaded) {
				debugLog('tabs', 'Fallback: no state sync received within 3s — the other tab(s) may no longer exist. Fetching course resource.');
				hasFetchedRef.current = true;
				fetchCourseResource();
			}
		}, 3000);

		return () => clearTimeout(fallbackTimer);
	}, [course?.isCourseLoaded]);

	// Heartbeat: periodically refresh this tab's timestamp so that
	// stale-entry pruning can distinguish live tabs from ones that were
	// discarded / suspended by Chrome without firing the unload event.
	// Also re-read the count on visibilitychange so the displayed number
	// corrects itself when the user switches back to this tab.
	useEffect(() => {
		const HEARTBEAT_INTERVAL_MS = 60_000; // 1 minute
		const interval = setInterval(() => {
			if (tabIdRef.current) {
				touchTab(courseId, tabIdRef.current);
			}
		}, HEARTBEAT_INTERVAL_MS);

		const onVisibilityChange = () => {
			if (document.visibilityState === 'visible') {
				// Touch our own heartbeat immediately (it may have been frozen)
				if (tabIdRef.current) {
					touchTab(courseId, tabIdRef.current);
				}
				// Re-read triggers pruning of dead entries, updating the count
				setTabsOpenedNumber(getCourseTabsOpened(courseId));
			}
		};

		document.addEventListener('visibilitychange', onVisibilityChange);

		return () => {
			clearInterval(interval);
			document.removeEventListener('visibilitychange', onVisibilityChange);
		};
	}, [courseId]);

	useEffect(() => {
		if (changes) {
			const activeChanges = hasAnyActiveChanges(changes) || activeFileUploads > 0;
			const multipleTabsOpened = getCourseTabsOpened(courseId) > 1;

			setShowExitPrompt(activeChanges && !multipleTabsOpened);
		}
	}, [changes, tabsOpenedNumber, activeFileUploads]);

	useBeforeUnload((event: BeforeUnloadEvent) => {
		if (showExitPrompt) event.preventDefault();
	});

	const unloadHandler = () => {
		if (tabIdRef.current) {
			unregisterTab(courseId, tabIdRef.current);
		}
	};

	const removeActiveUserHandler = (event) => {
		const roomName = getRoomNameByLocation(location.pathname);
		dispatch(removeActiveUsers({ courseId, room: roomName }));
	};

	const storageHandler = () => {
		const count = getCourseTabsOpened(courseId);
		const activeChanges = hasAnyActiveChanges(changes) || activeFileUploads > 0;

		setTabsOpenedNumber(count);
		setShowExitPrompt(activeChanges && count === 1);
	};

	useEventListener('storage', storageHandler);

	useEventListener('unload', unloadHandler);
	useEventListener('unload', removeActiveUserHandler);

	return (
		<Provider config={rollbarConfig}>
			<ErrorBoundary>
				<LoaderWrapper loading={!course || !course.isCourseLoaded || !course.isSubtreeLoaded}>
					{course?.isCourseLoaded && course?.isSubtreeLoaded ? (
						<>
							{!course?.policies.canUpdateResource && <ReadonlyAlert />}

							<Global styles={appGlobalStyles} />
							<ThemeProvider theme={themes[course.theme]}>
								<FontProvider />
								<div className={appContainer}>
									<Routing />

									<GlobalLoader isLoading={course.isLoading} />
									<ConflictUpdate courseId={courseId} />
								</div>
							</ThemeProvider>
						</>
					) : (
						<ForbiddenAlert />
					)}
				</LoaderWrapper>

				<ToastContainer />
			</ErrorBoundary>
		</Provider>
	);
};

export default EditorApp;
